/**
 * Shared `orgNode` key across OPP, WLP, FRC and HB. Extra segments are ignored
 * by apps that do not model them (e.g. sub-department).
 */
const SEPARATOR = ":";
const DIVISION_SEGMENT = "division";
const DEPARTMENT_SEGMENT = "department";
const SUB_DEPARTMENT_SEGMENT = "subDepartment";
/**
 * Segment names used by the apps for their own internal, non-shareable node ids
 * (e.g. `unit:23`). Such values must never be published to the shared key.
 */
const INTERNAL_ID_PREFIXES = [
    "unit",
    DIVISION_SEGMENT,
    DEPARTMENT_SEGMENT,
    SUB_DEPARTMENT_SEGMENT,
    "costCentre",
];
/**
 * True when the value is an application-internal node id (`unit:23`) rather
 * than a shareable composite key (`STO:556`).
 */
export function isInternalOrgNodeId(value) {
    if (value == null || value === "")
        return false;
    const separatorIndex = value.indexOf(SEPARATOR);
    if (separatorIndex < 1)
        return false;
    return INTERNAL_ID_PREFIXES.includes(value.substring(0, separatorIndex));
}
export function formatOrgNodeKey({ unitType, unitCode, divisionCode, departmentCode, subDepartmentCode, }) {
    if (unitType === "" || unitCode === "") {
        throw new Error("Cannot format an orgNode key without unit type and code");
    }
    let key = `${unitType}${SEPARATOR}${unitCode}`;
    if (divisionCode == null || divisionCode === "")
        return key;
    key += `${SEPARATOR}${DIVISION_SEGMENT}${SEPARATOR}${divisionCode}`;
    if (departmentCode == null || departmentCode === "")
        return key;
    key += `${SEPARATOR}${DEPARTMENT_SEGMENT}${SEPARATOR}${departmentCode}`;
    if (subDepartmentCode == null || subDepartmentCode === "")
        return key;
    return `${key}${SEPARATOR}${SUB_DEPARTMENT_SEGMENT}${SEPARATOR}${subDepartmentCode}`;
}
/**
 * Parses a shared `orgNode` value. Returns `undefined` for empty, internal or
 * malformed values so callers can safely ignore them.
 *
 * Codes on the returned key are never empty strings, so callers only need a
 * `!= null` check.
 */
export function parseOrgNodeKey(value) {
    if (value == null || value === "")
        return undefined;
    if (isInternalOrgNodeId(value))
        return undefined;
    const segments = value.split(SEPARATOR);
    const [unitType, unitCode] = segments;
    if (unitType == null ||
        unitType === "" ||
        unitCode == null ||
        unitCode === "") {
        return undefined;
    }
    let divisionCode;
    let departmentCode;
    let subDepartmentCode;
    for (let index = 2; index < segments.length - 1; index += 2) {
        const name = segments[index];
        const code = segments[index + 1];
        if (code == null || code === "")
            continue;
        if (name === DIVISION_SEGMENT)
            divisionCode = code;
        if (name === DEPARTMENT_SEGMENT)
            departmentCode = code;
        if (name === SUB_DEPARTMENT_SEGMENT)
            subDepartmentCode = code;
    }
    if (divisionCode == null && departmentCode != null) {
        return { unitType, unitCode };
    }
    if (departmentCode == null) {
        subDepartmentCode = undefined;
    }
    return {
        unitType,
        unitCode,
        divisionCode,
        departmentCode,
        subDepartmentCode,
    };
}
export const ORG_NODE_STORAGE_KEY = "orgNode";
export const ORG_NODE_CHANGED_EVENT = "orgNodeChanged";
const stripLeadingZeros = (code) => code.replace(/^0+(?=\d)/, "");
/**
 * True when two unit codes address the same unit. Callers must try an exact
 * match across all candidates first; this is the fallback, needed because OPP
 * profile data carries unpadded codes (`095`) while the org database pads them
 * (`00095`).
 */
export function isSameUnitCode(a, b) {
    if (a == null || a === "" || b == null || b === "")
        return false;
    if (a === b)
        return true;
    return stripLeadingZeros(a) === stripLeadingZeros(b);
}
const depthOf = (key) => {
    if (key.subDepartmentCode != null && key.subDepartmentCode !== "")
        return 4;
    if (key.departmentCode != null && key.departmentCode !== "")
        return 3;
    if (key.divisionCode != null && key.divisionCode !== "")
        return 2;
    return 1;
};
/**
 * True when `candidate` addresses the same branch as `other` but at a shallower
 * level (e.g. a division key against a department key of that division).
 *
 * An app that could only resolve part of an incoming path uses this to avoid
 * publishing its shallower selection back and truncating the shared value.
 */
export function isAncestorOrgNodeKey(candidate, other) {
    if (other === undefined)
        return false;
    if (candidate.unitType !== other.unitType ||
        candidate.unitCode !== other.unitCode) {
        return false;
    }
    const candidateDivision = candidate.divisionCode ?? "";
    const otherDivision = other.divisionCode ?? "";
    if (candidateDivision !== "" && candidateDivision !== otherDivision) {
        return false;
    }
    const candidateDepartment = candidate.departmentCode ?? "";
    const otherDepartment = other.departmentCode ?? "";
    if (candidateDepartment !== "" && candidateDepartment !== otherDepartment) {
        return false;
    }
    const candidateSubDepartment = candidate.subDepartmentCode ?? "";
    const otherSubDepartment = other.subDepartmentCode ?? "";
    if (candidateSubDepartment !== "" &&
        candidateSubDepartment !== otherSubDepartment) {
        return false;
    }
    return depthOf(candidate) < depthOf(other);
}
//# sourceMappingURL=org-node-key.mjs.map